import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { OrgEndorsedService } from '@app/orgs/services/org-endorsed.service';
import { PathwayAddContentService } from '@app/pathways/services/pathway-add-content.service';
import { SearchService } from '@app/search/services/search.service';
import { AuthService } from '@app/shared/services/auth.service';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { TargetsService } from '@app/shared/services/targets.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { OrgManagedContentService } from '../org-managed-notification/org-managed-content.service';
import { SearchFiltersStateService } from '../search-filters/search-filters-state.service';
import { SearchFilterId } from '../search-filters/search-filters.component';
import { AddBySearchBaseComponent } from './add-by-search-base.component';
import { AddBySearchService } from './add-by-search.service';

@Component({
  selector: 'dgx-add-by-search-mixed',
  templateUrl: './add-by-search-base.component.html',
})
export class AddBySearchMixedComponent extends AddBySearchBaseComponent {
  public static readonly modalName = 'search';
  public static readonly debounceDuration = 300;
  public static readonly typeFacetId = 'Type';
  public static readonly internalFacetId = 'Internal';
  public static readonly searchParams = {
    queryOffset: 0,
    itemsPerPage: 40,
  };

  @Input() public resourceType: 'Pathway' | 'Target';
  @Input() public closeSearchOnSelect: boolean = true;

  public i18n = this.translateService.instant([
    'Pathways_HoldForLater',
    'TargetResourcesForm_Selected',
    'TargetResourcesForm_SearchMixedPlaceholder',
  ]);
  public searchCatalogText =
    this.i18n.TargetResourcesForm_SearchMixedPlaceholder;
  public selectedText = this.i18n.TargetResourcesForm_Selected;
  public holdForLaterText = this.i18n.Pathways_HoldForLater;

  constructor(
    // Parent services for component being extended
    authService: AuthService,
    cdr: ChangeDetectorRef,
    ldFlagsService: LDFlagsService,
    orgEndorsedService: OrgEndorsedService,
    searchService: SearchService,
    orgManagedService: OrgManagedContentService,
    // Local-only services
    private addBySearchService: AddBySearchService,
    private pathwayAddContentService: PathwayAddContentService,
    private searchFiltersStateService: SearchFiltersStateService,
    private targetsService: TargetsService,
    private translateService: TranslateService
  ) {
    super(
      authService,
      cdr,
      ldFlagsService,
      orgEndorsedService,
      searchService,
      orgManagedService
    );
  }

  /**
   * Local implementation of updateButtonText.
   *
   * @param savedItemList
   */
  public updateButtonText(savedItemList: LearningResourceViewModel[]) {
    this.addItemsButtonText =
      this.pathwayAddContentService.updateButtonText(savedItemList);
  }

  /**
   * Local implementation of search.
   *
   * @param searchTerm
   * @returns
   */
  protected search(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.isSearchOpen = true;
    this.isSearching = true;

    const exclusionList = [];
    this.exclusionList?.forEach((item) => {
      exclusionList.push({
        resourceId: item.referenceId,
        resourceType: item.referenceType,
      });
    });
    this.savedItems.forEach((item) => {
      exclusionList.push({
        resourceId: item.resourceId,
        resourceType: item.resourceType,
      });
    });
    // Exclude itself in the search results if we are a plan
    if (this.resourceId) {
      exclusionList.push({
        resourceId: this.resourceId,
        resourceType: this.resourceType,
      });
    }

    return this.targetsService
      .searchMixedResources({
        term: this.searchTerm,
        targetId: this.resourceId,
        orgId: this.authService.authUser.defaultOrgId,
        facets: this.searchService.filtersToFacets(this.selectedFilters),
        ignorePreferredLanguage:
          this.searchFiltersStateService.ignorePreferredLanguage,
        exclusionList,
      })
      .pipe(
        finalize(() => {
          this.isSearching = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(({ results, facets, total }) => {
        this.searchResults = results as LearningResourceViewModel[];
        this.searchTotal = total;

        // Always display all `Type` subitems, otherwise bookmarked searches
        // and page reloads won't display any subitems that aren't pre-selected
        const configs = this.searchService.patchFacetConfig(
          SearchFilterId.Type,
          facets,
          { showEmptyOptions: true }
        );

        this.searchFilters = this.addBySearchService.facetsToFilters(
          facets,
          configs
        );
      });
  }

  /**
   * Local implementation of resetFilters.
   */
  protected resetFilters() {
    this.invalidateFilterCache = true;
    this.searchFiltersStateService.setIgnorePreferredLanguage(false);
    this.selectedFilters = [];
  }
}
